import { If, ListSkeleton, PageHeader } from "../../common/ui"
import { useCallback, useEffect, useState } from "react"
import { BillingLogListItem, BillingLogListResponse } from "../../types"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { DateTime } from "luxon"
import { DollarSign, FileText, Search, TrendingUp } from "lucide-react"
import { numberToUSD } from "../../common/utils"
import bbApi from "../../api"
import BillingTable from "./billing-table"
import BillingTrendChart from "./billing-trend-chart"
import PaginationWrapper from "../../common/ui/pagination/wrapper"

const generateMonthOptions = () => {
  const options: { value: string; label: string }[] = []
  const now = DateTime.now()
  for (let i = 1; i <= 12; i++) {
    const dt = now.minus({ months: i })
    options.push({
      value: dt.toFormat("yyyy-MM"),
      label: dt.toFormat("LLLL yyyy")
    })
  }
  return options
}

const MONTH_OPTIONS = generateMonthOptions()
const DEFAULT_MONTH = MONTH_OPTIONS[0].value

type StatusFilter =
  | ""
  | "initial"
  | "issued"
  | "paid"
  | "failed"
  | "overdue"
  | "cancelled"
  | "refunded"

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "initial", label: "Initial" },
  { value: "issued", label: "Issued" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "overdue", label: "Overdue" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" }
]

const PAGE_SIZE = 50

const SELECT_CLASS =
  "p-3 border border-neutral-200 rounded-md shadow-sm text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"

const Billing = () => {
  const [month, setMonth] = useState(DEFAULT_MONTH)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("")
  const [searchQuery, setSearchQuery] = useState("")
  const [excludeZero, setExcludeZero] = useState(true)
  const [sortDir, setSortDir] = useState<"asc" | "desc" | "">("")
  const [page, setPage] = useState(0)

  const [invoices, setInvoices] = useState<BillingLogListItem[]>([])
  const [total, setTotal] = useState(0)
  const [invoiceTotal, setInvoiceTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)
    try {
      const data: BillingLogListResponse = await bbApi.billing.fetchBillingLogs(
        {
          month: month || undefined,
          status: statusFilter || undefined,
          org_name: searchQuery.trim() || undefined,
          exclude_zero: excludeZero || undefined,
          sort: sortDir ? "total_value" : undefined,
          sort_dir: sortDir ? sortDir.toUpperCase() : undefined,
          page: page + 1,
          limit: PAGE_SIZE
        }
      )
      setInvoices(data.invoices)
      setTotal(data.total)
      setInvoiceTotal(data.invoice_total)
    } catch {
      setIsError(true)
      setInvoices([])
      setTotal(0)
      setInvoiceTotal(0)
    } finally {
      setIsLoading(false)
    }
  }, [month, statusFilter, searchQuery, excludeZero, sortDir, page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Reset to first page when filters change
  const handleMonthChange = (value: string) => {
    setMonth(value)
    setPage(0)
  }
  const handleStatusChange = (value: StatusFilter) => {
    setStatusFilter(value)
    setPage(0)
  }
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setPage(0)
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  if (isError) return <p>Failed to load data.</p>

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.billing}>
      <>
        <div className="p-6 pt-4 sm:pt-6 border-neutral-200">
          <PageHeader title="Billing" />
        </div>
        <div className="px-4 py-6 md:px-6 flex-1 bg-primary-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block font-medium text-sm mb-1">Month</label>
              <select
                value={month}
                onChange={e => handleMonthChange(e.target.value)}
                className={`${SELECT_CLASS} w-full`}
              >
                {MONTH_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-medium text-sm mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={e =>
                  handleStatusChange(e.target.value as StatusFilter)
                }
                className={`${SELECT_CLASS} w-full`}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-medium text-sm mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder="Search by org name..."
                  className="p-3 pl-9 border border-neutral-200 rounded-md shadow-sm text-sm w-full focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer sm:self-end sm:pb-3">
              <input
                type="checkbox"
                checked={excludeZero}
                onChange={e => {
                  setExcludeZero(e.target.checked)
                  setPage(0)
                }}
                className="w-4 h-4 rounded border-neutral-300 text-primary-400 focus:ring-primary-400"
              />
              <span className="text-sm text-neutral-600">
                Exclude $0 invoices
              </span>
            </label>
          </div>

          <BillingTrendChart
            status={statusFilter || undefined}
            orgName={searchQuery || undefined}
            excludeZero={excludeZero || undefined}
            onMonthClick={handleMonthChange}
          />

          {!isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center gap-3 bg-white rounded-lg px-5 py-4 border border-neutral-200">
                <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg bg-primary-alt-400/10">
                  <DollarSign className="w-5 h-5 text-primary-alt-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Invoice Total
                  </p>
                  <p className="text-xl font-semibold text-primary-alt-400 tabular-nums truncate">
                    {numberToUSD(invoiceTotal)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-lg px-5 py-4 border border-neutral-200">
                <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg bg-neutral-100">
                  <FileText className="w-5 h-5 text-neutral-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Invoices
                  </p>
                  <p className="text-xl font-semibold text-neutral-800 tabular-nums">
                    {total}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-lg px-5 py-4 border border-neutral-200">
                <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-lg bg-primary-400/10">
                  <TrendingUp className="w-5 h-5 text-primary-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Average
                  </p>
                  <p className="text-xl font-semibold text-neutral-800 tabular-nums truncate">
                    {numberToUSD(total > 0 ? invoiceTotal / total : 0)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <ListSkeleton
            containerClassName={`rounded-lg overflow-hidden ${
              isLoading ? "" : "hidden"
            }`}
          />

          <div hidden={isLoading}>
            <If
              condition={!isLoading}
              then={
                <BillingTable
                  invoices={invoices}
                  sortDir={sortDir}
                  onSortAmount={() => {
                    setSortDir(d =>
                      d === "" ? "desc" : d === "desc" ? "asc" : ""
                    )
                    setPage(0)
                  }}
                />
              }
            />
            {totalPages > 1 && (
              <PaginationWrapper
                pageIndex={page}
                pageSize={PAGE_SIZE}
                rowCount={total}
                disabledPrev={page === 0}
                disabledNext={page >= totalPages - 1}
                onClickPrev={() => setPage(p => Math.max(0, p - 1))}
                onClickNext={() => setPage(p => p + 1)}
                setPageIndex={setPage}
              />
            )}
          </div>
        </div>
      </>
    </RequireRole>
  )
}

export default Billing
